/**
 * PropertyCard Component
 *
 * Professional property card design with clean, metric-focused layout
 * featuring prominent imagery and key investment data
 */

import React from 'react';

export interface FieldVisibility {
  show_target_return?: boolean;
  show_share_price?: boolean;
  show_investor_count?: boolean;
  show_property_value?: boolean;
  show_funding_progress?: boolean;
}

export interface PropertyCardData {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  imageUrl: string;
  propertyType: string;
  price: number;
  totalShares: number;
  availableShares: number;
  targetReturn: number;
  investorCount: number;
  fundedPercentage: number;
  status: 'available' | 'funded' | 'sold_out';
  tags?: string[];
  tabContent?: Record<string, string>;
  fieldVisibility?: FieldVisibility;
}

interface PropertyCardProps {
  property: PropertyCardData;
  onClick?: (property: PropertyCardData) => void;
}

const PropertyCardBase: React.FC<PropertyCardProps> = ({ property, onClick }) => {
  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const isLicenseOffer = property.tags?.includes('license-offer');
  const filledSlots = Math.max(0, (property.totalShares || 0) - (property.availableShares || 0));

  // Visibility helper: unset key = visible; explicitly false = hidden
  const fv = property.fieldVisibility;
  const show = (key: keyof FieldVisibility) => !fv || fv[key] !== false;

  return (
    <div onClick={() => onClick?.(property)} className="group bg-white rounded-xl border border-border hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer h-full flex flex-col">
        {/* Image Section */}
        <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
          <img
            src={property.imageUrl}
            alt={property.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />

          {/* Progress Bar */}
          {property.status === 'available' && property.fundedPercentage > 0 && (isLicenseOffer || show('show_funding_progress')) && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-sm p-3">
              <div className="flex items-center justify-between text-white text-xs font-medium mb-1.5">
                {isLicenseOffer ? (
                  <>
                    <span>{filledSlots} / {property.totalShares.toLocaleString()} Slots Filled</span>
                    <span>{property.fundedPercentage}% Allocated</span>
                  </>
                ) : (
                  <>
                    <span>{property.fundedPercentage}% Funded</span>
                    <span>{property.availableShares.toLocaleString()} shares left</span>
                  </>
                )}
              </div>
              <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${property.fundedPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Fully Funded Badge */}
          {(property.status === 'funded' || property.status === 'sold_out') && (
            <>
              <div className="absolute top-3 right-3 bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                Fully Funded
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-emerald-700/80 backdrop-blur-sm p-3">
                <div className="flex items-center justify-between text-white text-xs font-medium mb-1.5">
                  <span>Fully Funded</span>
                  <span>100%</span>
                </div>
                <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: '100%' }} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Content Section */}
        <div className="p-5 flex-1 flex flex-col">
          {/* Property Name */}
          <h3 className="text-lg font-bold text-foreground mb-2 line-clamp-1">
            {property.name}
          </h3>

          {/* Subtitle / Location */}
          {isLicenseOffer ? (
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-3">
              {property.address}
            </p>
          ) : (
            <div className="text-sm text-muted-foreground mb-4">
              {property.city}, {property.state}
            </div>
          )}

          {/* Metrics Grid */}
          {isLicenseOffer ? (
            <div className="grid grid-cols-2 gap-4 mt-auto">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Slots</div>
                <div className="text-lg font-bold text-foreground">{filledSlots} / {property.totalShares}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Slots Remaining</div>
                <div className="text-lg font-bold text-foreground">{property.availableShares}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Annual Allocation</div>
                <div className="text-lg font-bold text-foreground">{formatCurrency(property.price)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Cohort</div>
                <div className="text-lg font-bold text-foreground">Founding</div>
              </div>
            </div>
          ) : property.status === 'funded' || property.status === 'sold_out' ? (
            show('show_property_value') ? (
              <div className="mt-auto">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Property Value</div>
                  <div className="text-lg font-bold text-foreground">{formatCurrency(property.price)}</div>
                </div>
              </div>
            ) : null
          ) : (() => {
            const visibleMetrics = [
              show('show_target_return') && (
                <div key="tr">
                  <div className="text-xs text-muted-foreground mb-1">Target Return</div>
                  <div className="text-lg font-bold text-foreground">{property.targetReturn ?? 0}%</div>
                </div>
              ),
              show('show_share_price') && (
                <div key="sp">
                  <div className="text-xs text-muted-foreground mb-1">Per Share</div>
                  <div className="text-lg font-bold text-foreground">
                    {formatCurrency((property.price || 0) / (property.totalShares || 1))}
                  </div>
                </div>
              ),
              show('show_investor_count') && (
                <div key="ic">
                  <div className="text-xs text-muted-foreground mb-1">Investors</div>
                  <div className="text-lg font-bold text-foreground">{property.investorCount ?? 0}</div>
                </div>
              ),
              show('show_property_value') && (
                <div key="pv">
                  <div className="text-xs text-muted-foreground mb-1">Property Value</div>
                  <div className="text-lg font-bold text-foreground">{formatCurrency(property.price)}</div>
                </div>
              ),
            ].filter(Boolean);
            return visibleMetrics.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 mt-auto">
                {visibleMetrics}
              </div>
            ) : null;
          })()}
        </div>
      </div>
  );
};

export const PropertyCard = React.memo(PropertyCardBase);
